import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { dashboardAPI, agentsAPI, knowledgeAPI } from '../services/api';
import { DashboardStats, ConversationActivity, Agent } from '../types';
import { TrendingUp, MessageSquare, Clock, CheckCircle, AlertTriangle, Inbox, Ticket as TicketIcon, Users, UserPlus, Bot, PenLine, Download, BookOpen, Eye, ThumbsUp, ThumbsDown, Sparkles } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, Legend } from 'recharts';
import AgentEditorModal from '../components/AgentEditorModal';
import InlineNotice from '../components/orchestrator/InlineNotice';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useBilling } from '../contexts/BillingContext';

// Setup Tour — the dock widget itself is mounted at app shell (Layout.tsx)
// so it persists across routes. This page only retains:
//   1. The "Setup Guide" button (dispatches a CustomEvent the dock catches).
//   2. The `?setup=1` URL force-open handler (same event dispatch).
// Both controls render on BOTH builds (the dock is build-aware internally).
const OPEN_TOUR_EVENT = 'prodagi:open-setup-tour';

type Preset = '7d' | '14d' | '30d' | '3m' | '6m' | '1y' | 'custom';

const DATE_PRESETS: { label: string; value: Preset }[] = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 14 days', value: '14d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 3 months', value: '3m' },
  { label: 'Last 6 months', value: '6m' },
  { label: 'Last 1 year', value: '1y' },
  { label: 'Custom range', value: 'custom' },
];

function getDateRange(preset: Preset, customStart: string, customEnd: string) {
  const end = new Date();
  const start = new Date();
  if (preset === '7d') start.setDate(end.getDate() - 7);
  else if (preset === '14d') start.setDate(end.getDate() - 14);
  else if (preset === '30d') start.setDate(end.getDate() - 30);
  else if (preset === '3m') start.setMonth(end.getMonth() - 3);
  else if (preset === '6m') start.setMonth(end.getMonth() - 6);
  else if (preset === '1y') start.setFullYear(end.getFullYear() - 1);
  else if (preset === 'custom') return { start_date: customStart, end_date: customEnd };
  return {
    start_date: start.toISOString().split('T')[0],
    end_date: end.toISOString().split('T')[0],
  };
}

function exportToCSV(rows: unknown[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0] as Record<string, unknown>);
  const csv = [
    headers.join(','),
    ...rows.map(row => headers.map(h => JSON.stringify((row as Record<string, unknown>)[h] ?? '')).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Plan-AutoInstallCommerceAgent §5e follow-up (2026-06-11) — session-scoped
// dismissal for the force-open AgentEditorModal path. The seed agent stays
// flagged `needs_onboarding=true` until the merchant SAVES the modal
// (cleared server-side), so without a parent-side dismissal record the
// force-open effect re-fires on every billing.agents change and traps the
// user in the wizard after Cancel. We track dismissed slugs per workspace
// in localStorage so the dismissal survives a refresh too. The entry
// becomes inert once the agent's flag clears server-side; we don't bother
// pruning. Returning to the seed agent post-dismissal is via the Agents
// page Edit button (separate AgentEditorModal mount, not gated here).
const dismissalKey = (wsId: string) => `prodagi_onboarding_dismissed_${wsId}`;

const readDismissed = (wsId: string): string[] => {
  try {
    const raw = localStorage.getItem(dismissalKey(wsId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

const appendDismissed = (wsId: string, agentSlug: string) => {
  try {
    const current = readDismissed(wsId);
    if (current.includes(agentSlug)) return;
    localStorage.setItem(dismissalKey(wsId), JSON.stringify([...current, agentSlug]));
  } catch {
    // localStorage write failure (private mode, quota) — fall through; the
    // user will see the modal again on next dep change but nothing crashes.
  }
};

const ExportButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
  >
    <Download className="w-3.5 h-3.5" />
    Download CSV
  </button>
);

const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { setUser } = useAuth();
  const { activeWorkspace } = useWorkspace();
  // Plan-AutoInstallCommerceAgent §5e (Storm 439407b 2026-05-26): the
  // `needs_onboarding` flag lives on `billing.agents[]` (Shopify-only),
  // NOT on `agentsAPI.list()`. We read it here and join back to the
  // full Agent doc (held in `fullAgents` state) so AgentEditorModal
  // still gets initialConfig/agentEmail/s3Assets from the agents
  // endpoint.
  const { billing } = useBilling();
  const [fullAgents, setFullAgents] = useState<Agent[]>([]);
  const [agents, setAgents] = useState<{ id: string; agent_id: string; name: string }[]>([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activity, setActivity] = useState<ConversationActivity[]>([]);
  const [csatMetrics, setCsatMetrics] = useState<{ distribution: Record<number, number>; average: number | null; total: number; surveys_sent: number; surveys_returned: number; survey_return_rate: number } | null>(null);
  // The four outcome buckets are required HERE (not on the wire type) — they are
  // normalised to 0 at ingest, so every consumer below can treat them as total.
  const [deflectionTrend, setDeflectionTrend] = useState<{ week: string; total: number; deflected: number; rate: number; resolved: number; escalated: number; abandoned: number; unknown: number }[]>([]);
  const [csatTrend, setCsatTrend] = useState<{ week: string; avg_csat: number; total_ratings: number }[]>([]);
  const [escalationBreakdown, setEscalationBreakdown] = useState<{ reason: string; count: number }[]>([]);
  const [resolutionTime, setResolutionTime] = useState<{ avg_resolution_time_ms: number; trend: { week: string; avg_ms: number; count: number }[] } | null>(null);
  const [channelSplit, setChannelSplit] = useState<{ chat: { total: number; resolved: number; resolution_rate: number }; email: { total: number; resolved: number; resolution_rate: number } } | null>(null);
  const [agentComparison, setAgentComparison] = useState<{ agent_id: string; agent_name: string; total: number; deflection_rate: number; escalation_rate: number; abandonment_rate: number; avg_csat: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Plan-AutoInstallCommerceAgent §5e (2026-05-26) — when an agent
  // carries `needs_onboarding=true` (Shopify auto-created), the modal
  // opens in EDIT mode pre-populated with that agent. Null = new-agent
  // mode (existing zero-agent path).
  const [onboardingAgent, setOnboardingAgent] = useState<Agent | null>(null);
  const [hcAnalytics, setHcAnalytics] = useState<{
    totals: { total_views: number; total_helpful: number; total_not_helpful: number; overall_helpfulness_pct: number };
    top_viewed: { article_id: string; title: string; view_count: number; helpful_count: number; not_helpful_count: number; helpfulness_pct: number }[];
    worst_rated: { article_id: string; title: string; view_count: number; helpful_count: number; not_helpful_count: number; helpfulness_pct: number }[];
  } | null>(null);

  // Date filter state
  const [selectedPreset, setSelectedPreset] = useState<Preset>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Handle post-checkout success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      showToast('Subscription activated! Welcome to your new plan.', 'success');
      import('../services/api').then(({ authAPI }) => {
        authAPI.getCurrentUser().then((res) => {
          setUser(res.data);
        }).catch(() => {});
      });
      navigate('/dashboard', { replace: true });
    }
  }, [searchParams, navigate, showToast, setUser]);

  const fetchData = useCallback(async (days: number, agentIds?: string[]) => {
    const agentFilter = agentIds && agentIds.length > 0 ? agentIds : undefined;
    try {
      const [statsRes, activityRes, agentsRes, csatRes, deflectionRes, csatTrendRes, escalationRes, resolutionRes, channelRes, agentCompRes] = await Promise.all([
        dashboardAPI.getStats(days, agentFilter),
        dashboardAPI.getConversationActivity(days, agentFilter),
        agentsAPI.list(),
        dashboardAPI.getCsatMetrics(days, agentFilter),
        dashboardAPI.getDeflectionTrend(days, agentFilter),
        dashboardAPI.getCsatTrend(days, agentFilter),
        dashboardAPI.getEscalationBreakdown(days, agentFilter),
        dashboardAPI.getResolutionTime(days, agentFilter),
        dashboardAPI.getChannelSplit(days, agentFilter),
        dashboardAPI.getAgentComparison(days, agentFilter),
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
      setFullAgents(agentsRes.data);
      const agentList = agentsRes.data.map((a: any) => ({ id: a.id, agent_id: a.agent_id, name: a.config?.agent_name || a.agent_id }));
      setAgents(agentList);
      if (csatRes.data) setCsatMetrics(csatRes.data);
      // Outcome-split fields default to 0 until the backend ships them, so the
      // new series render flat rather than throwing on undefined arithmetic.
      if (deflectionRes.data) setDeflectionTrend(
        Array.isArray(deflectionRes.data)
          ? deflectionRes.data.map(d => ({
              ...d,
              resolved: d.resolved ?? 0,
              escalated: d.escalated ?? 0,
              abandoned: d.abandoned ?? 0,
              unknown: d.unknown ?? 0,
            }))
          : []
      );
      if (csatTrendRes.data) setCsatTrend(Array.isArray(csatTrendRes.data) ? csatTrendRes.data : []);
      if (escalationRes.data) setEscalationBreakdown(Array.isArray(escalationRes.data) ? escalationRes.data : []);
      if (resolutionRes.data) setResolutionTime(resolutionRes.data);
      if (channelRes.data) setChannelSplit(channelRes.data);
      if (agentCompRes.data) setAgentComparison(
        Array.isArray(agentCompRes.data)
          ? agentCompRes.data.map(a => ({ ...a, abandonment_rate: a.abandonment_rate ?? 0 }))
          : []
      );

      if (agentsRes.data.length === 0) {
        setOnboardingAgent(null);
        setShowOnboarding(true);
      }
      // The needs_onboarding force-open path lives in a separate effect
      // below — it needs both `billing.agents[]` (the flag source) AND
      // `fullAgents` (the config/email/s3 hydration) to be resolved.

      // Fetch HC analytics for first agent (best-effort)
      if (agentsRes.data.length > 0) {
        try {
          const hcRes = await knowledgeAPI.analytics(agentsRes.data[0].agent_id);
          setHcAnalytics(hcRes.data);
        } catch {
          // HC analytics endpoint may not exist yet
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset the agent filter when the active workspace changes. Skip the
  // initial mount so we don't generate a fresh `[]` reference that would
  // re-trigger the main fetch effect for nothing — the main effect's
  // `selectedAgentIds` dep already covers the initial fetch.
  const prevWorkspaceIdRef = useRef(activeWorkspace?.id);
  useEffect(() => {
    if (prevWorkspaceIdRef.current !== activeWorkspace?.id) {
      prevWorkspaceIdRef.current = activeWorkspace?.id;
      setSelectedAgentIds([]);
    }
  }, [activeWorkspace?.id]);

  useEffect(() => {
    if (selectedPreset === 'custom' && (!customStart || !customEnd)) return;
    const { start_date, end_date } = getDateRange(selectedPreset, customStart, customEnd);
    const days = Math.max(1, Math.ceil((new Date(end_date).getTime() - new Date(start_date).getTime()) / 86400000));
    fetchData(days, selectedAgentIds);
    // `activeWorkspace?.id` is intentionally NOT a dep — workspace changes
    // flow into this effect indirectly via the reset effect above clearing
    // `selectedAgentIds`. Including it here would cause a duplicate fetch.
  }, [selectedPreset, customStart, customEnd, selectedAgentIds, fetchData]);

  // Plan-AutoInstallCommerceAgent §5e (Storm 439407b 2026-05-26) —
  // force-open AgentEditorModal in EDIT mode for any agent flagged
  // `needs_onboarding=true` on the billing payload (Shopify auto-
  // created seed). Read the flag from `billing.agents[]` and join by
  // `agent_id` slug back to the full Agent doc from `fullAgents`
  // (which carries config/email/s3_assets the modal needs to hydrate).
  // Skips when the zero-agent new-mode modal is already open (the
  // `agentsAPI.list()` length check in fetchData handles that path).
  useEffect(() => {
    if (showOnboarding) return; // don't stomp the zero-agent path
    if (!billing?.agents?.length) return;
    if (!fullAgents.length) return;
    const wsId = activeWorkspace?.id;
    if (!wsId) return;
    // Skip slugs the user has Cancel-dismissed in this workspace (read
    // fresh each effect run; the modal's onClose appends to the list
    // before flipping isOpen false, so the next dep-change pass sees it).
    const dismissed = readDismissed(wsId);
    const flagged = billing.agents
      .filter((a) => a.needs_onboarding === true)
      .filter((a) => !dismissed.includes(a.agent_id))
      .map((b) => fullAgents.find((f) => f.agent_id === b.agent_id))
      .filter((a): a is Agent => Boolean(a))
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
    if (flagged.length > 0) {
      setOnboardingAgent(flagged[0]);
      setShowOnboarding(true);
    }
  }, [billing?.agents, fullAgents, showOnboarding, activeWorkspace?.id]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    window.location.reload();
  };

  // Shopify Setup Tour `?setup=1` URL force-open — dispatches the same
  // custom event the dock listens for at app shell (Layout.tsx). Auto-show
  // on first authed render lives inside the dock itself; this page only
  // owns the explicit force-open trigger and the in-header button below.
  // Skips while AgentEditorModal is up so the dock doesn't pop over the
  // agent-creation wizard.
  useEffect(() => {
    if (searchParams.get('setup') !== '1') return;
    if (showOnboarding) return;
    window.dispatchEvent(new CustomEvent(OPEN_TOUR_EVENT));
    const next = new URLSearchParams(searchParams);
    next.delete('setup');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, showOnboarding]);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <AgentEditorModal
        isOpen={showOnboarding}
        onClose={() => {
          // Force-edit path only: append the slug to the workspace's
          // dismissal list so the effect above doesn't immediately
          // re-open the modal on the next billing.agents tick. The
          // zero-agent new-mode path has `onboardingAgent === null`,
          // so the guard skips it and the close stays vanilla.
          if (onboardingAgent && activeWorkspace?.id) {
            appendDismissed(activeWorkspace.id, onboardingAgent.agent_id);
          }
          setShowOnboarding(false);
        }}
        onComplete={handleOnboardingComplete}
        {...(onboardingAgent
          ? {
              isEdit: true,
              initialConfig: onboardingAgent.config,
              agentId: onboardingAgent.agent_id,
              agentEmail: onboardingAgent.email,
              s3Assets: onboardingAgent.s3_assets,
              helpCenterSlug: onboardingAgent.help_center_slug,
            }
          : {})}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-4xl font-bold gradient-text">Dashboard</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent(OPEN_TOUR_EVENT))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
            title="Re-open the setup walkthrough"
          >
            <Sparkles className="w-4 h-4" />
            Setup Guide
          </button>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Date Filter Bar */}
      <div className="card p-3">
        <div className="flex flex-wrap gap-1.5">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              onClick={() => setSelectedPreset(p.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                selectedPreset === p.value
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {selectedPreset === 'custom' && (
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">From</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="input-field py-1.5 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">To</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="input-field py-1.5 text-sm"
              />
            </div>
          </div>
        )}
        {agents.length > 1 && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">AI Agents:</span>
            <button
              onClick={() => setSelectedAgentIds([])}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                selectedAgentIds.length === 0
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All
            </button>
            {agents.map(agent => (
              <button
                key={agent.id}
                onClick={() => {
                  setSelectedAgentIds(prev =>
                    prev.includes(agent.id)
                      ? prev.filter(id => id !== agent.id)
                      : [...prev, agent.id]
                  );
                }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedAgentIds.includes(agent.id)
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {agent.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="stat-card group hover:scale-105 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Total Conversations</p>
              <p className="text-4xl font-bold gradient-text mt-2">
                {stats?.total_conversations || 0}
              </p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card group hover:scale-105 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Avg Response Time</p>
              <p className="text-4xl font-bold gradient-text mt-2">
                {stats?.avg_response_time_ms.toFixed(0) || 0}
                <span className="text-lg">ms</span>
              </p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Clock className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card group hover:scale-105 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Auto-Resolution Rate</p>
              <p className="text-4xl font-bold gradient-text mt-2">
                {stats?.auto_resolution_rate.toFixed(1) || 0}
                <span className="text-lg">%</span>
              </p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card group hover:scale-105 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Peak Support Time</p>
              <p className="text-2xl font-bold gradient-text mt-2">
                {stats?.peak_support_time || 'N/A'}
              </p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card group hover:scale-105 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Escalation Rate</p>
              <p className="text-4xl font-bold gradient-text mt-2">
                {stats?.escalation_rate.toFixed(1) || 0}
                <span className="text-lg">%</span>
              </p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card group hover:scale-105 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Active Agents</p>
              <p className="text-4xl font-bold gradient-text mt-2">
                {stats?.active_agents || 0}
              </p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        {/* Average CSAT card */}
        <div className="stat-card group hover:scale-105 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Avg CSAT Score</p>
              <p className="text-4xl font-bold gradient-text mt-2">
                {stats?.average_csat != null ? stats.average_csat.toFixed(1) : '—'}
                {stats?.average_csat != null && <span className="text-lg">/5</span>}
              </p>
              {typeof stats?.total_csat_ratings === 'number' && stats.total_csat_ratings > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stats.total_csat_ratings} rating{stats.total_csat_ratings !== 1 ? 's' : ''}</p>
              )}
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <span className="text-3xl">★</span>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Stats */}
      {stats && (
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <TicketIcon className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold gradient-text">Ticket Overview</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Open Tickets', value: stats.open_tickets, color: 'from-blue-500 to-blue-600', icon: Inbox },
              { label: 'In Progress', value: stats.in_progress_tickets, color: 'from-amber-500 to-amber-600', icon: Clock },
              { label: 'Resolved Today', value: stats.resolved_today, color: 'from-green-500 to-green-600', icon: CheckCircle },
              { label: 'SLA Breached', value: stats.sla_breached_tickets, color: 'from-red-500 to-red-600', icon: AlertTriangle },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <a key={card.label} href="/tickets" className="stat-card group hover:scale-105 transition-transform duration-300 block">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{card.label}</p>
                      <p className="text-4xl font-bold gradient-text mt-2">{card.value}</p>
                    </div>
                    <div className={`w-16 h-16 bg-gradient-to-br ${card.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Contacts Overview */}
      {stats?.contacts && (
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold gradient-text">Contacts Overview</h2>
          </div>

          {/* Contacts stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Contacts', value: stats.contacts.total_contacts, color: 'from-blue-500 to-cyan-600', icon: Users },
              { label: 'New Today', value: stats.contacts.new_contacts_today, color: 'from-green-500 to-emerald-600', icon: UserPlus },
              { label: 'Auto-Captured', value: stats.contacts.auto_captured, color: 'from-purple-500 to-violet-600', icon: Bot },
              { label: 'Manually Added', value: stats.contacts.manual, color: 'from-gray-500 to-slate-600', icon: PenLine },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <a key={card.label} href="/contacts" className="stat-card group hover:scale-105 transition-transform duration-300 block">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{card.label}</p>
                      <p className="text-4xl font-bold gradient-text mt-2">{card.value}</p>
                    </div>
                    <div className={`w-16 h-16 bg-gradient-to-br ${card.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          {/* Label & Source breakdown side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Label */}
            {stats.contacts.by_label.length > 0 && (
              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">By Label</h3>
                  <ExportButton onClick={() => exportToCSV(stats.contacts.by_label, 'contacts_by_label.csv')} />
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.contacts.by_label} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis dataKey="label" type="category" fontSize={12} tickLine={false} axisLine={false} width={80} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#1f2937' }}
                    />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                      {stats.contacts.by_label.map((entry, idx) => {
                        const colors: Record<string, string> = { Customer: '#10b981', Active: '#3b82f6', New: '#8b5cf6', Lead: '#6b7280' };
                        return <Cell key={idx} fill={colors[entry.label] || '#6b7280'} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* By Source */}
            {stats.contacts.by_source.length > 0 && (
              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">By Source</h3>
                  <ExportButton onClick={() => exportToCSV(stats.contacts.by_source, 'contacts_by_source.csv')} />
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.contacts.by_source} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis dataKey="label" type="category" fontSize={12} tickLine={false} axisLine={false} width={80} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#1f2937' }}
                    />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                      {stats.contacts.by_source.map((entry, idx) => {
                        const colors: Record<string, string> = { email: '#8b5cf6', chat: '#3b82f6', manual: '#6b7280' };
                        return <Cell key={idx} fill={colors[entry.label] || '#6b7280'} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSAT Distribution Chart */}
      {csatMetrics && csatMetrics.total > 0 && (() => {
        const csatData = [1, 2, 3, 4, 5].map((score) => ({
          label: `${score} ★`,
          count: csatMetrics.distribution[score] ?? 0,
        }));
        const csatColors: Record<number, string> = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#84cc16', 5: '#22c55e' };
        return (
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-primary text-xl">★</span>
              <h2 className="text-xl font-bold gradient-text">CSAT Score Distribution</h2>
              <span className="ml-2 text-sm text-gray-400">({csatMetrics.total} total ratings · avg {csatMetrics.average?.toFixed(1)}/5)</span>
            </div>

            {/* Survey return rate mini-cards */}
            <div className="grid grid-cols-3 gap-4 mb-5">
              {[
                { label: 'Surveys Sent', value: csatMetrics.surveys_sent, suffix: '', color: 'from-blue-500 to-cyan-600' },
                { label: 'Surveys Returned', value: csatMetrics.surveys_returned, suffix: '', color: 'from-green-500 to-emerald-600' },
                { label: 'Return Rate', value: csatMetrics.survey_return_rate, suffix: '%', color: csatMetrics.survey_return_rate >= 50 ? 'from-green-500 to-teal-600' : 'from-amber-500 to-orange-600' },
              ].map((card) => (
                <div key={card.label} className="stat-card py-3 px-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{card.label}</p>
                  <p className="text-3xl font-bold gradient-text mt-1">{card.value}<span className="text-base">{card.suffix}</span></p>
                </div>
              ))}
            </div>

            <div className="card-hover">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Rating Distribution</span>
                <ExportButton onClick={() => exportToCSV(csatData, 'csat_distribution.csv')} />
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={csatData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="label" fontSize={14} tickLine={false} axisLine={false} />
                  <YAxis fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#1f2937' }}
                    formatter={(value: number) => [value, 'Responses']}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {csatData.map((_, idx) => (
                      <Cell key={idx} fill={csatColors[idx + 1]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })()}

      {/* 1. Deflection Trend — 12 Week */}
      {deflectionTrend.length > 0 && (
        <div className="card-hover">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-2xl font-bold gradient-text">AI Deflection Rate — 12 Week Trend</h2>
            <ExportButton
              onClick={() => exportToCSV(
                deflectionTrend.map(d => ({
                  'Week': d.week,
                  'Total Conversations': d.total,
                  'Resolved': d.resolved,
                  'Escalated': d.escalated,
                  'Abandoned': d.abandoned,
                  'Unknown': d.unknown,
                  'Deflection Rate %': (d.rate * 100).toFixed(1),
                  'Abandonment Rate %': (d.total > 0 ? (d.abandoned / d.total) * 100 : 0).toFixed(1),
                })),
                'deflection_trend.csv',
              )}
            />
          </div>
          {/* The deflection number DROPS the day the resolved-only split ships.
              Without this line that reads as a product regression. */}
          <div className="mb-5">
            <InlineNotice tone="info">
              Deflection counts conversations the customer confirmed as finished. Conversations
              that simply went quiet are shown separately as <strong>Abandoned</strong> — they
              were previously counted as deflected.
            </InlineNotice>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={deflectionTrend.map(d => ({
              ...d,
              rate_pct: d.rate * 100,
              abandon_pct: d.total > 0 ? (d.abandoned / d.total) * 100 : 0,
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="week" stroke="#374151" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#374151" fontSize={12} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#1f2937' }}
                formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
              />
              <Legend />
              <Line type="monotone" name="Deflected" dataKey="rate_pct" stroke="#22c55e" strokeWidth={3} dot={{ fill: '#22c55e', r: 4, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }} />
              <Line type="monotone" name="Abandoned" dataKey="abandon_pct" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', r: 4, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 2. CSAT Trend — 12 Week */}
      {csatTrend.length > 0 && (
        <div className="card-hover">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold gradient-text">CSAT Score Trend</h2>
            <ExportButton onClick={() => exportToCSV(csatTrend, 'csat_trend.csv')} />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={csatTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="week" stroke="#374151" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#374151" fontSize={12} tickLine={false} axisLine={false} domain={[0, 5]} tickFormatter={(v) => `${v}`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#1f2937' }}
                formatter={(value: number) => [`${value.toFixed(2)}/5`, 'Avg CSAT']}
              />
              <Line type="monotone" dataKey="avg_csat" stroke="#eab308" strokeWidth={3} dot={{ fill: '#eab308', r: 4, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 3. Escalation Breakdown */}
      <div className="card-hover">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold gradient-text">Escalation Reasons</h2>
          {escalationBreakdown.length > 0 && (
            <ExportButton onClick={() => exportToCSV(escalationBreakdown, 'escalation_breakdown.csv')} />
          )}
        </div>
        {escalationBreakdown.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">No escalations recorded</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, escalationBreakdown.length * 40)}>
            <BarChart data={escalationBreakdown} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
              <XAxis type="number" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis dataKey="reason" type="category" fontSize={12} tickLine={false} axisLine={false} width={120} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#1f2937' }}
              />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} fill="#f97316" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 4. Resolution Time */}
      {resolutionTime && (
        <div>
          <h2 className="text-2xl font-bold gradient-text mb-4">Ticket Resolution Time</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="stat-card group hover:scale-105 transition-transform duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Avg Resolution</p>
                  <p className="text-4xl font-bold gradient-text mt-2">
                    {(() => {
                      const ms = resolutionTime.avg_resolution_time_ms;
                      if (ms < 60000) return `${(ms / 1000).toFixed(0)}s`;
                      const totalMin = Math.floor(ms / 60000);
                      const h = Math.floor(totalMin / 60);
                      const m = totalMin % 60;
                      return h > 0 ? `${h}h ${m}m` : `${m}m`;
                    })()}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Clock className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
            {resolutionTime.trend.length > 0 && (
              <div className="card-hover md:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Weekly Trend</span>
                  <ExportButton onClick={() => exportToCSV(resolutionTime.trend, 'resolution_time_trend.csv')} />
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={resolutionTime.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="week" stroke="#374151" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#374151" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => {
                      const totalMin = Math.floor(v / 60000);
                      const h = Math.floor(totalMin / 60);
                      const m = totalMin % 60;
                      return h > 0 ? `${h}h` : `${m}m`;
                    }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#1f2937' }}
                      formatter={(value: number) => {
                        const totalMin = Math.floor(value / 60000);
                        const h = Math.floor(totalMin / 60);
                        const m = totalMin % 60;
                        return [h > 0 ? `${h}h ${m}m` : `${m}m`, 'Avg Resolution'];
                      }}
                    />
                    <Line type="monotone" dataKey="avg_ms" stroke="#06b6d4" strokeWidth={3} dot={{ fill: '#06b6d4', r: 4, strokeWidth: 2, stroke: '#fff' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 5. Channel Performance */}
      {channelSplit && (
        <div>
          <h2 className="text-2xl font-bold gradient-text mb-4">Channel Performance</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {([
              { key: 'chat', label: 'Chat', data: channelSplit.chat, color: 'from-blue-500 to-indigo-600', icon: MessageSquare },
              { key: 'email', label: 'Email', data: channelSplit.email, color: 'from-purple-500 to-pink-600', icon: Inbox },
            ] as const).map((ch) => (
              <div key={ch.key} className="stat-card group hover:scale-105 transition-transform duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{ch.label}</p>
                    <p className="text-4xl font-bold gradient-text mt-2">{ch.data.total}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{(ch.data.resolution_rate * 100).toFixed(1)}% resolved</p>
                  </div>
                  <div className={`w-16 h-16 bg-gradient-to-br ${ch.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                    <ch.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. Per-Agent Performance */}
      {agentComparison.length > 1 && (
        <div className="card-hover">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold gradient-text">Per-Agent Performance</h2>
            <ExportButton
              onClick={() => exportToCSV(
                agentComparison.map(a => ({
                  'Agent': a.agent_name,
                  'Total Conversations': a.total,
                  'Deflection Rate %': (a.deflection_rate * 100).toFixed(1),
                  'Escalation Rate %': (a.escalation_rate * 100).toFixed(1),
                  'Abandonment Rate %': (a.abandonment_rate * 100).toFixed(1),
                  'Avg CSAT': a.avg_csat,
                })),
                'agent_performance.csv',
              )}
            />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {/* Green stays exclusively on deflection. Abandonment takes violet:
                every amber/yellow warning step collides with the escalation
                orange below the readable-separation floor (amber ΔE 9.6,
                yellow 14.8 vs the 15 minimum), so a warm warning hue here
                would be indistinguishable from the bar beside it. */}
            <BarChart data={agentComparison.map(a => ({ ...a, deflection_pct: a.deflection_rate * 100, escalation_pct: a.escalation_rate * 100, abandonment_pct: a.abandonment_rate * 100 }))} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="agent_name" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis fontSize={12} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: 'none', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#1f2937' }}
                formatter={(value: number) => `${value.toFixed(1)}%`}
              />
              <Legend />
              <Bar dataKey="deflection_pct" name="Deflection %" fill="#22c55e" radius={[6, 6, 0, 0]} />
              <Bar dataKey="escalation_pct" name="Escalation %" fill="#f97316" radius={[6, 6, 0, 0]} />
              <Bar dataKey="abandonment_pct" name="Abandonment %" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 7. Conversation Activity Chart */}
      <div className="card-hover">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold gradient-text">Conversation Activity</h2>
          {activity.length > 0 && (
            <ExportButton onClick={() => exportToCSV(activity, 'conversation_activity.csv')} />
          )}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={activity}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#374151" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#374151" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${value}`} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                color: '#1f2937'
              }}
              itemStyle={{ color: '#1f2937' }}
              labelStyle={{ color: '#111827', fontWeight: 'bold' }}
            />
            <Line
              type="monotone"
              dataKey="count"
              stroke="url(#colorGradient)"
              strokeWidth={3}
              dot={{ fill: '#667eea', r: 4, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
            />
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#667eea" />
                <stop offset="100%" stopColor="#764ba2" />
              </linearGradient>
            </defs>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Help Center Performance */}
      <div className="card-hover">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-bold gradient-text">Help Center Performance</h2>
          </div>
          {hcAnalytics && hcAnalytics.top_viewed.length > 0 && (
            <ExportButton onClick={() => exportToCSV(hcAnalytics.top_viewed, 'help_center_analytics.csv')} />
          )}
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Eye className="w-4 h-4" />
              Total Views
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{(hcAnalytics?.totals.total_views ?? 0).toLocaleString()}</p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <ThumbsUp className="w-4 h-4" />
              Helpfulness
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {hcAnalytics && (hcAnalytics.totals.total_helpful + hcAnalytics.totals.total_not_helpful) > 0
                ? `${hcAnalytics.totals.overall_helpfulness_pct.toFixed(1)}%`
                : '—'}
            </p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <MessageSquare className="w-4 h-4" />
              Total Feedback
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{(hcAnalytics ? hcAnalytics.totals.total_helpful + hcAnalytics.totals.total_not_helpful : 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Top viewed + worst rated side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Most Viewed</h3>
            {hcAnalytics && hcAnalytics.top_viewed.length > 0 ? (
              <div className="space-y-2">
                {hcAnalytics.top_viewed.slice(0, 3).map((a, i) => (
                  <div key={a.article_id} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                      <span className="text-sm text-gray-900 dark:text-white truncate">{a.title}</span>
                    </div>
                    <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                      <Eye className="w-3 h-3" />
                      {a.view_count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic p-2.5">No articles yet</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Needs Improvement</h3>
            {hcAnalytics && hcAnalytics.worst_rated.length > 0 ? (
              <div className="space-y-2">
                {hcAnalytics.worst_rated.slice(0, 3).map((a, i) => (
                  <div key={a.article_id} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                      <span className="text-sm text-gray-900 dark:text-white truncate">{a.title}</span>
                    </div>
                    <span className="flex items-center gap-1 text-xs text-red-500 dark:text-red-400 flex-shrink-0 ml-2">
                      <ThumbsDown className="w-3 h-3" />
                      {a.helpfulness_pct.toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic p-2.5">No articles yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Quickstart */}
      <div className="card-hover">
        <h2 className="text-2xl font-bold gradient-text mb-4">Quickstart</h2>
        <div className="space-y-3">
          <a href="/agents" className="block p-5 glass rounded-xl hover:scale-105 transition-all duration-300 group">
            <h3 className="font-semibold text-gray-900 dark:text-white group-hover:gradient-text transition-all">Create Your First AI Agent</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Set up a custom support agent for your business</p>
          </a>
          <a href="/knowledge" className="block p-5 glass rounded-xl hover:scale-105 transition-all duration-300 group">
            <h3 className="font-semibold text-gray-900 dark:text-white group-hover:gradient-text transition-all">Add Knowledge Sources</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Upload files or scrape URLs to train your agent</p>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
